<template>
  <v-dialog v-model="isOpen" max-width="640" persistent :fullscreen="isMobile">
    <v-card :class="{ 'sync-failure-modal': isMobile }">
      <v-card-title class="d-flex justify-space-between align-center">
        <span class="d-flex align-center">
          <v-icon class="mr-2" color="warning">mdi-cloud-alert</v-icon>
          Sincronización incompleta
        </span>
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          aria-label="Cerrar"
          @click="close"
        />
      </v-card-title>

      <v-card-text class="sync-failure-modal__body">
        <v-alert type="warning" variant="tonal" class="mb-4">
          {{ summary }}
        </v-alert>

        <div class="sync-failure-list">
          <div
            v-for="chapter in failures"
            :key="chapter.chapterId"
            class="sync-failure-item"
          >
            <div class="sync-failure-item__content">
              <div class="text-subtitle-1">{{ chapter.title }}</div>
              <p class="sync-failure-item__reason mb-2">{{ chapter.reason }}</p>
              <p class="text-caption text-medium-emphasis mb-0">
                {{ wordsLabel(chapter.wordsCount) }}
                · Último cambio: {{ formatChangeDate(chapter.changedAt) }}
              </p>
            </div>
            <v-tooltip location="top">
              <template #activator="{ props: tooltipProps }">
                <v-btn
                  v-bind="tooltipProps"
                  class="sync-failure-item__open"
                  icon
                  variant="text"
                  color="primary"
                  aria-label="Ir al Editor"
                  @click="openEditor(chapter.chapterId)"
                >
                  <v-icon>mdi-pencil-outline</v-icon>
                </v-btn>
              </template>
              <span class="text-primary">Ir al Editor</span>
            </v-tooltip>
          </div>
        </div>

        <ColumnButtons
          primary-text="Cerrar"
          :show-secondary="false"
          @primary-click="close"
        />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from 'vue'
import { useRouter } from 'vue-router'
import { useDisplay } from 'vuetify'

import ColumnButtons from '@/components/baseComponents/ColumnButtons.vue'

interface SyncFailureChapter {
  chapterId: string
  title: string
  reason: string
  wordsCount: number
  changedAt: string
}

export default defineComponent({
  name: 'SyncFailureModal',

  components: {
    ColumnButtons,
  },

  props: {
    show: {
      type: Boolean,
      default: false,
    },
    projectId: {
      type: String,
      default: '',
    },
    projectTitle: {
      type: String,
      default: '',
    },
    syncedCount: {
      type: Number,
      default: 0,
    },
    totalCount: {
      type: Number,
      default: 0,
    },
    failures: {
      type: Array as PropType<SyncFailureChapter[]>,
      default: () => [],
    },
  },

  emits: ['update:show'],

  setup(props, { emit }) {
    const isMobile = useDisplay().mobile
    const router = useRouter()

    const isOpen = computed({
      get: () => props.show,
      set: (value: boolean) => emit('update:show', value),
    })

    const summary = computed(() => {
      const verb = props.syncedCount === 1 ? 'Se sincronizó' : 'Se sincronizaron'
      const chapterWord = props.totalCount === 1 ? 'capítulo respaldado' : 'capítulos respaldados'
      const title = props.projectTitle.trim()
      const projectLabel = title ? `del proyecto ${title}` : 'del proyecto'
      return `${verb} solo ${props.syncedCount} de ${props.totalCount} ${chapterWord} ${projectLabel}.`
    })

    const close = () => {
      emit('update:show', false)
    }

    const openEditor = (chapterId: string) => {
      if (!props.projectId || !chapterId) return
      emit('update:show', false)
      void router.push({
        name: 'project',
        params: { projectId: props.projectId },
        query: { tab: 'editor', chapterId },
      })
    }

    const wordsLabel = (count: number) => (
      count === 1 ? '1 palabra' : `${count} palabras`
    )

    const formatChangeDate = (value: string) => {
      if (!value) return 'Sin fecha'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return 'Sin fecha'
      return date.toLocaleString('es', { dateStyle: 'medium', timeStyle: 'short' })
    }

    return {
      isMobile,
      isOpen,
      summary,
      close,
      openEditor,
      wordsLabel,
      formatChangeDate,
    }
  },
})
</script>

<style scoped>
.sync-failure-modal {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.sync-failure-modal__body {
  overflow-y: auto;
  max-height: 70vh;
}

.sync-failure-modal .sync-failure-modal__body {
  flex: 1 1 auto;
  max-height: none;
}

.sync-failure-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sync-failure-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 8px 12px 16px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 12px;
  background: rgba(var(--v-theme-on-surface), 0.03);
}

.sync-failure-item__content {
  flex: 1 1 auto;
  min-width: 0;
}

.sync-failure-item__reason {
  margin: 4px 0 0;
  color: rgba(var(--v-theme-on-surface), 0.7);
  font-size: 0.875rem;
  line-height: 1.4;
}

.sync-failure-item__open {
  flex-shrink: 0;
}
</style>
